import { ButtonGroup, Dropdown, DropdownButton, Spinner } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslations } from "use-intl";
import { getSubAccountChart$ } from "@/store/sub_accounts";
import { useSubAccount } from "@/hooks";
import { useAppDispatch } from "@/store";
import { formatHash } from "@/utils/to-hash";
import style from "./index.module.scss";

const TIMES = ["10MIN", "HOUR", "DAY"];

// Inline hashrate chart for Screen 3 (single sub-account) - same
// GET .../observer/hashrate_chart/ data and ApexCharts config that used to
// live behind the "График хешрейта" modal (src/features/modals/
// statistic-details.tsx, now removed as dead code once this replaced its
// only call site). Moved inline to match the mockup, which shows the chart
// directly on the page rather than behind a button.
//
// Known limitation: this endpoint is live-only for every partner except
// SBI (SbiObserverHandler reads a stored snapshot; Antpool/ViaBTC/
// Spiderpool always call the partner API - sub_accounts/api/v1/handlers/
// observer_handler.py). Without real Antpool credentials, this chart only
// renders real data for demo-sbi-* sub-accounts locally - the same
// constraint already documented for the worker donut.
export function HashrateChart() {
  const [chartState, setChartState] = useState({
    series: [{ name: "Hashrate: ", data: [] as any }],
    options: {
      chart: {
        id: "area-datetime",
        type: "area",
        height: 300,
        toolbar: { show: false },
        zoom: { autoScaleYaxis: true },
      },
      dataLabels: { enabled: false },
      markers: { size: 3, colors: ["#3a83f1"], strokeColor: "#3a83f1", strokeWidth: 2 },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          datetimeFormatter: {
            year: "yyyy",
            month: "MMM 'yy",
            day: "dd MMM",
            hour: "HH:mm",
          },
        },
      },
      yaxis: {
        labels: { formatter: (val: any) => formatHash(val) },
      },
      stroke: { colors: ["#3a83f1"], width: 2 },
      tooltip: { x: { format: "dd MMM yyyy HH:mm" } },
      fill: {
        type: "gradient",
        gradient: { shade: "light", opacityFrom: 0.4, opacityTo: 0.05 },
      },
    },
  });
  const dispatch = useAppDispatch();
  const { currentSubAccount } = useSubAccount();
  const [loading, setLoading] = useState(true);
  const i18n = useTranslations("cabinet.statistics");
  const [currentTime, setCurrentTime] = useState("10MIN");
  const promise = useRef<any>();

  useEffect(() => {
    if (!currentSubAccount) {
      return;
    }
    promise.current = getGraphic(currentTime);
    return () => {
      if (promise.current) {
        promise.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTime, currentSubAccount]);

  const getGraphic = (interval: string) => {
    if (promise.current) {
      promise.current.abort();
    }
    setLoading(true);
    const dispatchReq = dispatch(
      getSubAccountChart$({ sub_account_name: currentSubAccount, interval }),
    );
    dispatchReq
      .unwrap()
      .then((res: any) => {
        setLoading(false);
        const { hashrates, now } = res;
        if (!hashrates?.length) {
          setChartState((prev) => ({ ...prev, series: [{ data: [], name: "Hashrate: " }] }));
          return;
        }
        const newArray = hashrates;
        const stepMinutes =
          ({ "10MIN": 10, HOUR: 60, DAY: 60 * 24 } as Record<string, number>)[
            interval
          ] || 10;
        const data: [Date, number][] = [];
        for (let x = newArray.length - 1; x >= 0; x--) {
          const newDate = new Date(now);
          newDate.setMinutes(
            newDate.getMinutes() - (newArray.length - x) * stepMinutes,
          );
          data.push([newDate, newArray[x]]);
        }
        setChartState((prev) => ({ ...prev, series: [{ data, name: "Hashrate: " }] }));
      })
      .catch(() => {
        setLoading(false);
        setChartState((prev) => ({ ...prev, series: [{ data: [], name: "Hashrate: " }] }));
      });
    return dispatchReq;
  };

  return (
    <div>
      <div className={style.header}>
        <h5>{i18n("graphic")}</h5>
        <DropdownButton
          as={ButtonGroup}
          size="sm"
          variant="outline-secondary"
          title={currentTime}
        >
          {TIMES.map((item, idx) => (
            <Dropdown.Item
              eventKey={idx}
              key={idx}
              active={currentTime === item}
              onClick={() => setCurrentTime(item)}
            >
              {item}
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </div>
      {loading ? (
        <div className={style.placeholder}>
          <Spinner />
        </div>
      ) : !chartState.series[0].data.length ? (
        <div className={style.placeholder}>{i18n("chartEmpty")}</div>
      ) : (
        <Chart
          options={chartState.options as any}
          series={chartState.series}
          type="area"
          height={300}
        />
      )}
    </div>
  );
}
